import {useDropzone} from 'react-dropzone';
import React, {useCallback} from 'react';
import {Cancel, CloudUpload} from '@mui/icons-material';
import {IconButton, Paper, Stack, Tooltip, Typography} from '@mui/material';
import IconDeleteButton from '../Button/IconDeletebutton';
import {isFilesExceedTheDuration} from '../utlis/Audio/GetAudioDuration';
import {truncateText} from '../../pages/Processing-center/ProcessingTabContent';

export type IUploadFileType = File & {
  url?: string;
};
const UploadFiles = ({
  files,
  setFiles,
  max,
  height,
  width,
  maxDurationInSec,
  maxSizeInMb,
}: {
  files: IUploadFileType[];
  setFiles: React.Dispatch<React.SetStateAction<IUploadFileType[]>>;
  max?: number;
  height?: number | string;
  width?: number | string;
  maxDurationInSec?: number;
  maxSizeInMb?: number;
}) => {
  const onDrop = async (files: IUploadFileType[]) => {
    if (
      maxDurationInSec &&
      (await isFilesExceedTheDuration(files, maxDurationInSec))
    ) {
      //If the uploaded song length is  more than the given length validation then this function will terminate
      return;
    }
    if (
      maxSizeInMb &&
      files.some(file => file.size > maxSizeInMb * 1024 * 1024)
    ) {
      //If the uploaded file more than the given size validation then this function will terminate
      return;
    }
    const mutatedFiles: IUploadFileType[] = [];
    if (max === 1 && files.length > 0) {
      const file = files[0];
      if (file) {
        file.url = URL.createObjectURL(file);
        mutatedFiles.push(file);
      }
    } else {
      mutatedFiles.push(
        ...files.map(file => {
          file.url = URL.createObjectURL(file);
          return file;
        }),
      );
    }
    setFiles(prev => {
      let finalFiles = [...mutatedFiles, ...prev].filter(
        (file, index, source) =>
          source.findIndex(f => f.name === file.name) === index,
      );
      if (max && finalFiles.length > max) {
        finalFiles = [...prev];
      }
      return finalFiles.sort((a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
      );
    });
  };

  const {getRootProps, getInputProps} = useDropzone({
    accept: {
      'audio/mpeg': [],
    },
    maxFiles: max ? max : undefined,
    onDrop,
  });
  const handleDeleteAll = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      setFiles([]);
    },
    [setFiles],
  );
  return (
    <Paper
      id='upload-box'
      {...getRootProps()}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 2,
        borderRadius: 1,
        borderColor: 'primary.main',
        borderStyle: 'dashed',
        backgroundColor: 'background.paper',
        color: 'text.primary',
        outline: 'none',
        transition: 'border .24s ease-in-out',
        cursor: 'pointer',
        maxHeight: height || 250,
        maxWidth: width || 350,
        overflowY: 'auto',
        margin: 'auto', // Center the dropzone
      }}>
      {files.length ? (
        <Stack sx={{padding: 2}} width='inherit' height='inherit'>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='center'
            className='hidden-important'>
            <IconButton
              title='Delete All'
              aria-label='delete'
              color='error'
              onClick={handleDeleteAll}>
              <Cancel />
            </IconButton>
          </Stack>
          {files.map((file, index) => (
            <SongSelected
              key={file.url}
              file={file}
              setFiles={setFiles}
              index={index}
            />
          ))}
        </Stack>
      ) : (
        <Stack
          alignItems='center'
          sx={{
            px: 3,
            backgroundColor: 'background.paper',
            color: 'text.primary',
          }}>
          <input {...getInputProps()} />
          <CloudUpload sx={{fontSize: 48}} />
          <Typography
            variant='body1'
            sx={{
              mt: 1,
              color: 'text.primary',
              backgroundColor: 'background.paper',
            }}>
            Drag & drop a file here or click to select a file
          </Typography>
          {maxDurationInSec && (
            <Typography
              sx={{color: 'text.primary', backgroundColor: 'background.paper'}}>
              Maximum length of audio should be {maxDurationInSec} seconds
            </Typography>
          )}
          {maxSizeInMb && (
            <Typography
              sx={{color: 'text.primary', backgroundColor: 'background.paper'}}>
              Maximum size of file should be {maxSizeInMb}MB
            </Typography>
          )}
        </Stack>
      )}
    </Paper>
  );
};

export default UploadFiles;

function SongSelected({
  file,
  setFiles,
  index,
}: {
  file: File;
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
  index: number;
}) {
  const handleDelete = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      setFiles(prev => {
        const songs = [...prev];
        songs.splice(index, 1);
        return songs;
      });
    },
    [index, setFiles],
  );

  return (
    <Stack
      direction='row'
      mt={0.5}
      alignItems='center'
      justifyContent='space-between'>
      <Tooltip title={file.name}>
        <Typography>{truncateText(file.name, 20)}</Typography>
      </Tooltip>

      <IconDeleteButton onClick={handleDelete} />
    </Stack>
  );
}
